import React, { createContext, useContext, useRef, useCallback, useMemo } from 'react';

export type AutoLoginPayload = { type: 'credentials'; email: string; password: string } | { type: 'google' };

interface AutoLoginContextValue {
  setAutoLogin: (payload: AutoLoginPayload) => void;
  getAndClear: () => AutoLoginPayload | null;
  hasPayload: () => boolean;
}

const AutoLoginContext = createContext<AutoLoginContextValue>({
  setAutoLogin: () => {},
  getAndClear: () => null,
  hasPayload: () => false
});

export const AutoLoginProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const payloadRef = useRef<AutoLoginPayload | null>(null);

  const setAutoLogin = useCallback((payload: AutoLoginPayload): void => {
    payloadRef.current = payload;
  }, []);

  const getAndClear = useCallback((): AutoLoginPayload | null => {
    const value = payloadRef.current;
    payloadRef.current = null; // wipe immediately
    return value;
  }, []);

  const hasPayload = useCallback((): boolean => payloadRef.current !== null, []);

  const contextValue = useMemo(
    () => ({
      setAutoLogin,
      getAndClear,
      hasPayload
    }),
    [setAutoLogin, getAndClear, hasPayload]
  );

  return <AutoLoginContext.Provider value={contextValue}>{children}</AutoLoginContext.Provider>;
};

export const useAutoLogin = (): AutoLoginContextValue => useContext(AutoLoginContext);
